import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import { API, fmtDate } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Newspaper, ChevronDown, ChevronUp } from "lucide-react";

export default function NewsSection() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetch(`${API}/news`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPosts)
      .catch(() => {});
  }, []);

  const L = (p, field) => p[`${field}_${lang}`] || p[`${field}_en`] || "";

  return (
    <section id="news" className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="news-section">
      <Reveal>
        <div className="flex items-center gap-3">
          <Newspaper size={20} className="text-[#635BFF]" />
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#635BFF]">{t("news.eyebrow")}</p>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A2540] mt-3">
          {t("news.title")}
        </h2>
        <p className="text-[#425466] mt-4 max-w-2xl">{t("news.subtitle")}</p>
      </Reveal>

      {posts.length === 0 ? (
        <div className="mt-10 bg-[#F6F9FC] border border-dashed border-[#E6EBF1] rounded-2xl p-10 text-center">
          <p className="text-sm text-[#425466]" data-testid="news-empty">{t("news.empty")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <article
                data-testid={`news-card-${p.id}`}
                className="group bg-white border border-[#E6EBF1] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(10,37,64,0.12)]"
              >
                {p.image && (
                  <div className="h-44 overflow-hidden">
                    <img src={p.image} alt={L(p, "title")} loading="lazy"
                         className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-6">
                  <p className="text-[11px] font-ticker text-slate-400">{fmtDate(p.created_at)}</p>
                  <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-1.5 leading-snug">{L(p, "title")}</h3>
                  <p className="text-sm text-[#425466] mt-2 leading-relaxed">{L(p, "excerpt")}</p>
                  {open === p.id && (
                    <p className="text-sm text-[#425466] mt-3 leading-relaxed whitespace-pre-line border-t border-[#E6EBF1] pt-3">
                      {L(p, "body")}
                    </p>
                  )}
                  {L(p, "body") && (
                    <button
                      data-testid={`news-toggle-${p.id}`}
                      onClick={() => setOpen(open === p.id ? null : p.id)}
                      className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[#635BFF] hover:text-[#0A2540] transition-colors"
                    >
                      {t("news.readMore")} {open === p.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
}
